/* eslint-disable no-magic-numbers */
/**
 * Builds TGL transforms from an already-loaded geodeticX module. Kept free of
 * the geodeticX import so geometry workers can share it with the main thread.
 */

import type { TglShiftGridEntry } from '../processing/construct-geometry.types.js';
import type { TglGeodeticXModule, TglTransformFunction } from './tgl.types.js';

// Maximum extent of the map in meters
const MAX_EXTENT_METER = (2 * Math.PI * 6378137) / 2.0;

function metersToRadians(
  horizontalMeters: number,
  verticalMeters: number
): { x: number; y: number } {
  return {
    x: (horizontalMeters / MAX_EXTENT_METER) * Math.PI,
    y: Math.atan(Math.sinh((verticalMeters / MAX_EXTENT_METER) * Math.PI)),
  };
}

function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

function allocateString(module: TglGeodeticXModule, str: string): number {
  const encodedStr = new TextEncoder().encode(str);
  const len = encodedStr.length;
  const ptr = module._malloc(len + 1);
  const bytes = new Uint8Array(module.wasmMemory.buffer, ptr, len + 1);
  bytes.set(encodedStr);
  bytes[len] = 0;
  return ptr;
}

export function createTglTransformFunction(
  geodeticX: TglGeodeticXModule,
  csib: ArrayBuffer,
  shiftGrids?: Iterable<TglShiftGridEntry>
): TglTransformFunction {
  if (shiftGrids) {
    for (const grid of shiftGrids) {
      geodeticX.FS.writeFile(`/${grid.name}`, grid.data);
    }
  }

  // Path where TGL finds shift grid and geoid files.
  const geoPathPtr = allocateString(geodeticX, '/');
  geodeticX._geoSetGeodataPath(geoPathPtr);

  const sceneCsibPtr = geodeticX._malloc(csib.byteLength);
  const sceneCsibDataArray = new Uint8Array(
    geodeticX.wasmMemory.buffer,
    sceneCsibPtr,
    csib.byteLength
  );
  sceneCsibDataArray.set(new Uint8Array(csib));
  const sceneCsibDataContainer = new geodeticX.GEOCsibBlobContainer(
    sceneCsibPtr,
    csib.byteLength
  );

  const transformer = new geodeticX.GeodeticXTransformer();
  transformer.CreateWithCsib(sceneCsibDataContainer);
  geodeticX._free(sceneCsibPtr);

  return (x, y, sceneToLonLat) => {
    if (!sceneToLonLat) {
      const radiansP = metersToRadians(x, y);
      const localResult = transformer.Transform(
        geodeticX.geoCoordinateTypes.gctReferenceGlobalLLH,
        radiansP.y,
        radiansP.x,
        0,
        geodeticX.geoCoordinateTypes.gctOrientatedNEH
      );

      return {
        x: localResult[1],
        y: localResult[0],
        errorCode: localResult[3],
      };
    }

    const latLonResult = transformer.Transform(
      geodeticX.geoCoordinateTypes.gctOrientatedNEH,
      y,
      x,
      0,
      geodeticX.geoCoordinateTypes.gctReferenceGlobalLLH
    );

    return {
      x: radiansToDegrees(latLonResult[1]),
      y: radiansToDegrees(latLonResult[0]),
      errorCode: latLonResult[3],
    };
  };
}
